import { TermDto } from '@act/shared/data-transfer-objects';
import {
  ConsentIdentityStepConnector,
  ConsentMemberStepper,
  FlexContainer,
} from '@act/storybook';
import { Step, StepLabel, Typography } from '@mui/material';
import ConsentIdentityForm, {
  ConsentIdentityFormRef,
} from './ConsentIdentityForm';
import ConsentIdentityStepIcon from './ConsentIndentityStepIcon';
import ConsentTermContent from './ConsentTermContent';
import { STEPPER_SECTIONS } from './stepperSections';
import React, { forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import useBreakpoint from 'utilities/useBreakpoint.hook';

/**
 * Line width for the step connector; derived from layout (narrow vs wide), not from `sx` in feature code.
 * @param isNarrow - Whether the viewport uses the mobile/tablet connector width rule
 * @returns A percentage string for the horizontal connector line
 */
const connectorLineWidth = (isNarrow: boolean) =>
  isNarrow ? `${82 + STEPPER_SECTIONS.length * 10}%` : '101.5%';

interface ConsentIdentityStepperProps {
  term?: TermDto;
  isLastStep: boolean;
  activeStep: number;
  setActiveStep: (step: number) => void;
  onFormValidityChange?: (isValid: boolean) => void;
}

export interface ConsentIdentityStepperRef {
  submitForm: () => Promise<void>;
}

const ConsentIdentityStepper = forwardRef<
  ConsentIdentityStepperRef,
  ConsentIdentityStepperProps
>(
  (
    { term, isLastStep, activeStep, setActiveStep, onFormValidityChange },
    ref
  ) => {
    const { t } = useTranslation();
    const { mobile, tablet } = useBreakpoint();
    const lineWidth = connectorLineWidth(mobile || tablet);

    const formRef = React.useRef<ConsentIdentityFormRef>(null);

    useImperativeHandle(
      ref,
      () => ({
        submitForm: async () => {
          if (formRef.current) {
            return await formRef.current.submitForm();
          }
          throw new Error('Form ref not available');
        },
      }),
      []
    );

    return (
      <FlexContainer
        direction="column"
        gap={2}
        width="100%"
        height="auto"
        overflow="hidden"
        margin="0 0 16px 0"
      >
        <FlexContainer
          direction="row"
          justifyContent="center"
          width="100%"
          height="auto"
          overflowAllVisible
        >
          <Typography
            variant="subheadLarge"
            align="center"
            fontWeight={400}
            color="textPrimary"
          >
            {t(STEPPER_SECTIONS[activeStep])}
          </Typography>
        </FlexContainer>

        <ConsentMemberStepper
          activeStep={activeStep}
          connector={
            <ConsentIdentityStepConnector lineWidth={lineWidth} />
          }
        >
          {STEPPER_SECTIONS.map(label => (
            <Step key={label}>
              <StepLabel StepIconComponent={ConsentIdentityStepIcon} />
            </Step>
          ))}
        </ConsentMemberStepper>
        {!isLastStep && (
          <ConsentIdentityForm
            ref={formRef}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            onFormValidityChange={onFormValidityChange}
          />
        )}
        {isLastStep && <ConsentTermContent term={term} />}
      </FlexContainer>
    );
  }
);

export default ConsentIdentityStepper;
